import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  listReleases,
  getRelease,
  createRelease,
  startSuggestionQuicktask,
  updateSuggestion,
  setSuggestionRejected,
  addSuggestion,
  confirmRelease,
  retryReview,
  startAutoLoop,
  stopAutoLoop,
  forceRelease,
  cancelRelease,
  deleteRelease,
  getTask,
  saveReleaseInstructions,
  getAvailableModels,
  startFixBatch,
  updateReleaseSettings,
} from '../services/api';
import { Release, ReleaseFixMode, ReleaseStatus, ReleaseSuggestion, Task, TaskStatus } from '../types';
import { useTheme } from '../context/ThemeContext';
import HistorySearchBar, { TimeRange, TIME_RANGE_TO_OPTIONS } from '../components/HistorySearchBar';

const STATUS_LABEL: Record<ReleaseStatus, string> = {
  [ReleaseStatus.GENERATING_NOTES]: 'Generating release notes',
  [ReleaseStatus.NOTES_READY]: 'Notes ready - awaiting confirmation',
  [ReleaseStatus.AWAITING_TASKS]: 'Waiting for fix tasks',
  [ReleaseStatus.RE_REVIEWING]: 'Re-reviewing fixes',
  [ReleaseStatus.REVIEW_REJECTED]: 'Re-review NOT APPROVED',
  [ReleaseStatus.RELEASING]: 'Running /release-quest',
  [ReleaseStatus.RELEASED]: 'Released',
  [ReleaseStatus.FAILED]: 'Failed',
  [ReleaseStatus.CANCELED]: 'Canceled',
};

const STATUS_COLOR: Record<ReleaseStatus, string> = {
  [ReleaseStatus.GENERATING_NOTES]: '#3b82f6',
  [ReleaseStatus.NOTES_READY]: '#f59e0b',
  [ReleaseStatus.AWAITING_TASKS]: '#8b5cf6',
  [ReleaseStatus.RE_REVIEWING]: '#8b5cf6',
  [ReleaseStatus.REVIEW_REJECTED]: '#f97316',
  [ReleaseStatus.RELEASING]: '#3b82f6',
  [ReleaseStatus.RELEASED]: '#10b981',
  [ReleaseStatus.FAILED]: '#ef4444',
  [ReleaseStatus.CANCELED]: '#6b7280',
};

// Model list from /models/available is latest-first per family. Pick the first
// id containing `family` (e.g. "opus", "sonnet") as "the latest <family> model".
const latestModelOf = (models: string[], family: string): string | undefined =>
  models.find(m => m.toLowerCase().includes(family));

const FIX_MODE_LABEL: Record<ReleaseFixMode, string> = {
  batched: 'One agent fixes all (groups by area, decides its own subagents)',
  per_suggestion: 'One separate quicktask per suggestion',
};

const areaOf = (s: ReleaseSuggestion): string => (s.area || '').trim().toLowerCase() || 'general';

// Ordered [area, indexes[]] in first-seen order, every suggestion included.
const groupByArea = (suggestions: ReleaseSuggestion[]): Array<[string, number[]]> => {
  const order: string[] = [];
  const groups: Record<string, number[]> = {};
  suggestions.forEach((s, idx) => {
    const a = areaOf(s);
    if (!groups[a]) { groups[a] = []; order.push(a); }
    groups[a].push(idx);
  });
  return order.map(a => [a, groups[a]]);
};

// A suggestion is pending if it never got a task, or the task it got failed
// or was dismissed (e.g. the user canceled it to regroup into a batch instead)
// so it must be offered again rather than staying stuck on a dead task link.
const STALE_TASK_STATUSES: TaskStatus[] = [TaskStatus.FAILED, TaskStatus.DISMISSED];
const isPendingSuggestion = (s: ReleaseSuggestion, tasksMap: Record<number, Task>) => {
  if (s.rejected || !(s.text || '').trim()) return false;
  if (!s.task_id) return true;
  const t = tasksMap[s.task_id];
  return !!t && STALE_TASK_STATUSES.includes(t.status);
};

const isActiveStatus = (s: ReleaseStatus) =>
  s === ReleaseStatus.GENERATING_NOTES ||
  s === ReleaseStatus.AWAITING_TASKS ||
  s === ReleaseStatus.RE_REVIEWING ||
  s === ReleaseStatus.RELEASING;

const Releases: React.FC = () => {
  const { theme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const releaseIdParam = searchParams.get('release');

  const [releases, setReleases] = useState<Release[]>([]);
  const [selected, setSelected] = useState<Release | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [timeRange, setTimeRange] = useState<TimeRange>('30');
  const [confirming, setConfirming] = useState(false);
  const [tasksMap, setTasksMap] = useState<Record<number, Task>>({});
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [releaseModel, setReleaseModel] = useState<string>('');
  const [releaseModelTouched, setReleaseModelTouched] = useState(false);
  // Fix tasks spawned from suggestions: model + strategy. Persisted on the
  // selected release so the auto-loop honours them.
  const [fixModel, setFixModel] = useState<string>('');
  const [fixModelTouched, setFixModelTouched] = useState(false);
  const [fixMode, setFixMode] = useState<ReleaseFixMode>('batched');
  const [modalConfig, setModalConfig] = useState<{
    title: string;
    message: string;
    confirmLabel: string;
    resolve: (val: boolean) => void;
  } | null>(null);

  const showConfirm = useCallback((title: string, message: string, confirmLabel = 'Confirm'): Promise<boolean> => {
    return new Promise(resolve => {
      setModalConfig({ title, message, confirmLabel, resolve });
    });
  }, []);

  const loadReleases = useCallback(async () => {
    try {
      const list = await listReleases({
        search: search.trim() || undefined,
        ...TIME_RANGE_TO_OPTIONS(timeRange),
      });
      setReleases(list);
      return list;
    } catch (err) {
      console.error('Failed to load releases', err);
      return [];
    }
  }, [search, timeRange]);

  const refreshSelected = useCallback(async (id: number) => {
    try {
      const fresh = await getRelease(id);
      setSelected(fresh);
      // Refresh task statuses for this release's suggestions
      const taskIds = fresh.suggestions
        .map(s => s.task_id)
        .filter((id): id is number => typeof id === 'number');
      if (taskIds.length > 0) {
        const taskResults = await Promise.all(
          taskIds.map(tid => getTask(tid).catch(() => null))
        );
        const map: Record<number, Task> = {};
        taskResults.forEach(t => {
          if (t) map[t.id] = t;
        });
        setTasksMap(prev => ({ ...prev, ...map }));
      }
      return fresh;
    } catch (err) {
      console.error('Failed to refresh release', err);
      return null;
    }
  }, []);

  // Initial load
  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadReleases();
      setLoading(false);
    })();
  }, [loadReleases]);

  // Load model options once, defaulting the release picker to the latest Opus
  // model (fix tasks spawned from suggestions default to Sonnet instead, set
  // at submit time, no picker needed there).
  useEffect(() => {
    let cancelled = false;
    getAvailableModels()
      .then(models => {
        if (cancelled) return;
        const list = Array.isArray(models) ? models : [];
        setAvailableModels(list);
        if (!releaseModelTouched) {
          setReleaseModel(latestModelOf(list, 'opus') || '');
        }
        if (!fixModelTouched) {
          setFixModel(prev => prev || latestModelOf(list, 'sonnet') || '');
        }
      })
      .catch(() => {
        /* model picker is optional; ignore load failure */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open release from URL parameter
  useEffect(() => {
    if (releaseIdParam) {
      const id = parseInt(releaseIdParam, 10);
      if (!isNaN(id)) {
        refreshSelected(id);
      }
    } else {
      setSelected(null);
    }
  }, [releaseIdParam, refreshSelected]);

  // When a release is opened, adopt its persisted fix settings once (the 3s
  // poll must not keep overwriting what the user is picking).
  const selectedId = selected?.id;
  useEffect(() => {
    if (!selectedId) return;
    (async () => {
      try {
        const r = await getRelease(selectedId);
        if (r.fix_model) { setFixModel(r.fix_model); setFixModelTouched(true); }
        if (r.fix_mode) setFixMode(r.fix_mode);
        if (r.model) { setReleaseModel(r.model); setReleaseModelTouched(true); }
      } catch { /* pickers keep their defaults */ }
    })();
  }, [selectedId]);

  // Auto-poll if anything is active, the auto-loop is running, or there are
  // pending tasks
  useEffect(() => {
    const anyActive = releases.some(r => isActiveStatus(r.status) || r.auto_loop_active)
      || (selected && (isActiveStatus(selected.status) || selected.auto_loop_active));
    const hasPendingTasks = selected && selected.suggestions.some(s => {
      if (!s.task_id) return false;
      const t = tasksMap[s.task_id];
      return !t || t.status === TaskStatus.QUEUED || t.status === TaskStatus.IN_PROGRESS;
    });
    if (!anyActive && !hasPendingTasks) return;
    const interval = setInterval(async () => {
      await loadReleases();
      if (selected) {
        await refreshSelected(selected.id);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [releases, selected, tasksMap, loadReleases, refreshSelected]);

  const handleStartRelease = async (autoLoop = false) => {
    if (autoLoop && !await showConfirm(
      'Start and Run Autonomously',
      `Generate release notes, then without waiting for you: ${fixMode === 'per_suggestion' ? 'spawn one fix task per suggestion' : 'hand every suggestion to one fixing agent'}, wait, re-review, and run /release-quest if approved. Stops after 5 iterations or when you hit "Stop auto-loop".`,
      'Start and Run Autonomously',
    )) return;
    setCreating(true);
    try {
      const release = await createRelease({
        model: releaseModel || undefined,
        fixModel: fixModel || undefined,
        fixMode,
        autoLoop,
      });
      setSearchParams({ release: release.id.toString() });
      await loadReleases();
      await refreshSelected(release.id);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to start release');
    } finally {
      setCreating(false);
    }
  };

  const handleStartSuggestionTask = async (index: number) => {
    if (!selected) return;
    try {
      const updated = await startSuggestionQuicktask(selected.id, index, undefined, fixModel || undefined);
      setSelected(updated);
      await loadReleases();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to start quicktask');
    }
  };

  const handleStartFixBatch = async (scope: 'all' | 'area', area?: string) => {
    if (!selected) return;
    const pending = selected.suggestions.filter(s => isPendingSuggestion(s, tasksMap) && (scope === 'all' || areaOf(s) === area));
    const what = scope === 'all' ? `all ${pending.length} pending suggestions` : `the ${pending.length} pending "${area}" suggestion${pending.length === 1 ? '' : 's'}`;
    const how = fixMode === 'per_suggestion'
      ? 'one separate quicktask per suggestion'
      : 'ONE agent that orients once, works them grouped by area, and decides its own subagents';
    if (!await showConfirm('Start fixes', `Hand ${what} to ${how}?`, 'Start fixes')) return;
    try {
      const updated = await startFixBatch(selected.id, {
        scope, area, model: fixModel || undefined, mode: fixMode,
      });
      setSelected(updated);
      await loadReleases();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to start fix tasks');
    }
  };

  const persistFixSettings = async (patch: { fix_model?: string; fix_mode?: ReleaseFixMode; model?: string }) => {
    if (!selected) return;
    try {
      const updated = await updateReleaseSettings(selected.id, patch);
      setSelected(updated);
    } catch { /* picker still applies at click time via explicit params */ }
  };

  const handleFixModelChange = (value: string) => {
    setFixModelTouched(true);
    setFixModel(value);
    persistFixSettings({ fix_model: value });
  };

  const handleFixModeChange = (value: ReleaseFixMode) => {
    setFixMode(value);
    persistFixSettings({ fix_mode: value });
  };

  const handleUpdateSuggestion = async (index: number, text: string) => {
    if (!selected) return;
    try {
      const updated = await updateSuggestion(selected.id, index, text);
      setSelected(updated);
      await loadReleases();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to update suggestion');
    }
  };

  const handleAddSuggestion = async (text: string) => {
    if (!selected) return;
    try {
      const updated = await addSuggestion(selected.id, text);
      setSelected(updated);
      await loadReleases();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to add suggestion');
    }
  };

  const handleSetSuggestionRejected = async (index: number, rejected: boolean) => {
    if (!selected) return;
    try {
      const updated = await setSuggestionRejected(selected.id, index, rejected);
      setSelected(updated);
      await loadReleases();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to update suggestion');
    }
  };

  const handleConfirmRelease = async () => {
    if (!selected) return;
    if (!await showConfirm('Confirm Release', 'Confirm release? This will wait for any pending fix tasks, re-review them, and then run /release-quest.')) return;
    setConfirming(true);
    try {
      const updated = await confirmRelease(selected.id);
      setSelected(updated);
      await loadReleases();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to confirm release');
    } finally {
      setConfirming(false);
    }
  };

  const handleRetryReview = async () => {
    if (!selected) return;
    if (!await showConfirm('Re-run Review', 'Re-run the re-review now? Any new fix tasks you started will be waited on first.')) return;
    setConfirming(true);
    try {
      const updated = await retryReview(selected.id);
      setSelected(updated);
      await loadReleases();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to retry review');
    } finally {
      setConfirming(false);
    }
  };

  const handleStartAutoLoop = async () => {
    if (!selected) return;
    const how = fixMode === 'per_suggestion' ? 'spawn one fix task per pending suggestion' : 'hand every pending suggestion to one fixing agent';
    if (!await showConfirm('Run Autonomously', `Run autonomously until approved? Each pass will ${how}, wait, and re-review; approval runs /release-quest. Stops after 5 iterations or when you hit "Stop auto-loop".`, 'Run Autonomously')) return;
    try {
      const updated = await startAutoLoop(selected.id, {
        model: releaseModel || undefined,
        fix_model: fixModel || undefined,
        fix_mode: fixMode,
      });
      setSelected(updated);
      await loadReleases();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to start auto-loop');
    }
  };

  const handleStopAutoLoop = async () => {
    if (!selected) return;
    try {
      const updated = await stopAutoLoop(selected.id);
      setSelected(updated);
      await loadReleases();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to stop auto-loop');
    }
  };

  const handleForceRelease = async () => {
    if (!selected) return;
    if (!await showConfirm('Force Release', 'Force-release WITHOUT re-review approval? This bypasses the AI verdict and runs /release-quest immediately. Use only if you have manually verified the fixes.', 'Force Release')) return;
    try {
      const updated = await forceRelease(selected.id);
      setSelected(updated);
      await loadReleases();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to force release');
    }
  };

  const handleCancel = async () => {
    if (!selected) return;
    if (!await showConfirm('Cancel Release', 'Cancel this release?', 'Cancel Release')) return;
    try {
      const updated = await cancelRelease(selected.id);
      setSelected(updated);
      await loadReleases();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  const handleDelete = async (id: number) => {
    if (!await showConfirm('Delete Release', 'Delete this release record?', 'Delete')) return;
    try {
      await deleteRelease(id);
      if (selected?.id === id) {
        setSelected(null);
        setSearchParams({});
      }
      await loadReleases();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleSaveInstructions = async (instructions: string) => {
    if (!selected) return;
    const updated = await saveReleaseInstructions(selected.id, instructions);
    setSelected(updated);
  };

  const handleSelect = (release: Release) => {
    setSearchParams({ release: release.id.toString() });
    refreshSelected(release.id);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', color: theme.text }}>
        Loading...
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px', overflowX: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {selected && (
            <button
              className="mobile-back-btn"
              onClick={() => setSearchParams({})}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: `1px solid ${theme.border}`,
                backgroundColor: theme.surface,
                color: theme.text,
                cursor: 'pointer',
                fontSize: '14px',
                display: 'none',
              }}
            >
              ← Back
            </button>
          )}
          <div>
            <h1 style={{ color: theme.text, margin: 0 }}>Releases</h1>
            <p style={{ color: theme.textSecondary, margin: '4px 0 0', fontSize: '14px' }}>
              Drive a Quest release end-to-end: notes, fix tasks, re-review, /release-quest.
            </p>
          </div>
        </div>
        <div className="releases-header-actions" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={loadReleases}
            style={{
              padding: '12px 16px',
              borderRadius: '8px',
              border: `1px solid ${theme.border}`,
              backgroundColor: theme.surface,
              color: theme.text,
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Refresh
          </button>
          <select
            value={releaseModel}
            onChange={(e) => {
              setReleaseModelTouched(true);
              setReleaseModel(e.target.value);
            }}
            disabled={creating}
            title="Claude model for this release run"
            style={{
              padding: '12px 14px',
              borderRadius: '8px',
              border: `1px solid ${theme.border}`,
              backgroundColor: theme.surface,
              color: theme.text,
              fontSize: '14px',
              cursor: creating ? 'not-allowed' : 'pointer',
              minWidth: 0,
              maxWidth: '100%',
            }}
          >
            <option value="">System default</option>
            {availableModels.map(model => (
              <option key={model} value={model}>
                {model}
              </option>
            ))}
          </select>
          <button
            onClick={() => handleStartRelease(false)}
            disabled={creating}
            style={{
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: creating ? theme.border : '#10b981',
              color: '#fff',
              cursor: creating ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {creating ? 'Starting...' : '+ Start New Release'}
          </button>
          <button
            onClick={() => handleStartRelease(true)}
            disabled={creating}
            title="Generate notes, then fix all suggestions, re-review and release without waiting for you"
            style={{
              padding: '12px 20px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: creating ? theme.border : '#3b82f6',
              color: '#fff',
              cursor: creating ? 'not-allowed' : 'pointer',
              fontSize: '15px',
              fontWeight: 600,
            }}
          >
            {creating ? 'Starting...' : 'Start + Run Autonomously'}
          </button>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .releases-header-actions {
            width: 100%;
          }
          .releases-header-actions > button,
          .releases-header-actions > select {
            flex: 1 1 100%;
            width: 100%;
            box-sizing: border-box;
          }
          .mobile-back-btn {
            display: block !important;
          }
          .releases-list-panel {
            display: ${selected ? 'none' : 'block'} !important;
          }
          .releases-detail-panel {
            display: ${selected ? 'block' : 'none'} !important;
          }
        }
      `}</style>

      {modalConfig && (
        <ConfirmModal
          title={modalConfig.title}
          message={modalConfig.message}
          confirmLabel={modalConfig.confirmLabel}
          onConfirm={() => { setModalConfig(null); modalConfig.resolve(true); }}
          onCancel={() => { setModalConfig(null); modalConfig.resolve(false); }}
        />
      )}

      <div className={`responsive-grid${selected ? ' responsive-grid--detail' : ''}`}>
        {/* List of releases */}
        <div className="releases-list-panel">
          <div style={{ marginBottom: '16px' }}>
            <HistorySearchBar
              search={search}
              onSearchChange={setSearch}
              searchPlaceholder="Search title or release notes..."
              timeRange={timeRange}
              onTimeRangeChange={setTimeRange}
            />
          </div>
          {releases.length === 0 ? (
            <div style={{
              padding: '32px',
              textAlign: 'center',
              color: theme.textSecondary,
              backgroundColor: theme.surface,
              borderRadius: '8px',
              border: `1px solid ${theme.border}`,
            }}>
              <p>{search || timeRange !== '30' ? 'No releases match this search/range.' : 'No releases yet.'}</p>
              <p style={{ fontSize: '13px' }}>
                {search ? 'Try a different search term or widen the time range.' : 'Click "Start New Release" above.'}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {releases.map(r => (
                <div
                  key={r.id}
                  onClick={() => handleSelect(r)}
                  style={{
                    padding: '12px 14px',
                    backgroundColor: selected?.id === r.id ? theme.surfaceHover : theme.surface,
                    borderRadius: '8px',
                    border: `1px solid ${selected?.id === r.id ? STATUS_COLOR[r.status] : theme.border}`,
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <strong style={{ color: theme.text, fontSize: '14px' }}>{r.title}</strong>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <StatusBadge status={r.status} />
                    {isActiveStatus(r.status) && (
                      <span style={{
                        display: 'inline-block',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: STATUS_COLOR[r.status],
                        animation: 'pulse 1.5s infinite',
                      }} />
                    )}
                  </div>
                  <div style={{ color: theme.textSecondary, fontSize: '12px', marginTop: '4px' }}>
                    {new Date(r.created_at).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}
          <style>{`
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.4; }
            }
          `}</style>
        </div>

        {/* Selected release detail */}
        <div className="releases-detail-panel" style={{ display: selected ? 'block' : 'none', minWidth: 0 }}>
          {selected && (
            <ReleaseDetail
              release={selected}
              tasksMap={tasksMap}
              confirming={confirming}
              onStartSuggestionTask={handleStartSuggestionTask}
              onStartFixBatch={handleStartFixBatch}
              availableModels={availableModels}
              fixModel={fixModel}
              fixMode={fixMode}
              onFixModelChange={handleFixModelChange}
              onFixModeChange={handleFixModeChange}
              onUpdateSuggestion={handleUpdateSuggestion}
              onSetSuggestionRejected={handleSetSuggestionRejected}
              onAddSuggestion={handleAddSuggestion}
              onConfirm={handleConfirmRelease}
              onRetryReview={handleRetryReview}
              onStartAutoLoop={handleStartAutoLoop}
              onStopAutoLoop={handleStopAutoLoop}
              onForceRelease={handleForceRelease}
              onCancel={handleCancel}
              onDelete={() => handleDelete(selected.id)}
              onSaveInstructions={handleSaveInstructions}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const StatusBadge: React.FC<{ status: ReleaseStatus }> = ({ status }) => {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '12px',
        backgroundColor: STATUS_COLOR[status] + '22',
        color: STATUS_COLOR[status],
        fontSize: '12px',
        fontWeight: 600,
      }}
    >
      {STATUS_LABEL[status]}
    </span>
  );
};

const TaskStatusBadge: React.FC<{ task?: Task }> = ({ task }) => {
  const { theme } = useTheme();
  if (!task) {
    return <span style={{ color: theme.textSecondary, fontSize: '12px' }}>(loading...)</span>;
  }
  const colors: Record<string, string> = {
    [TaskStatus.QUEUED]: '#6b7280',
    [TaskStatus.IN_PROGRESS]: '#3b82f6',
    [TaskStatus.READY_FOR_REVIEW]: '#f59e0b',
    [TaskStatus.COMPLETED]: '#10b981',
    [TaskStatus.FAILED]: '#ef4444',
  };
  const c = colors[task.status] || '#6b7280';
  return (
    <span style={{
      padding: '2px 8px',
      borderRadius: '12px',
      backgroundColor: c + '22',
      color: c,
      fontSize: '11px',
      fontWeight: 600,
      textTransform: 'uppercase',
    }}>
      {task.status.replace('_', ' ')}
    </span>
  );
};

interface ReleaseDetailProps {
  release: Release;
  tasksMap: Record<number, Task>;
  confirming: boolean;
  onStartSuggestionTask: (index: number) => void;
  onStartFixBatch: (scope: 'all' | 'area', area?: string) => void;
  availableModels: string[];
  fixModel: string;
  fixMode: ReleaseFixMode;
  onFixModelChange: (value: string) => void;
  onFixModeChange: (value: ReleaseFixMode) => void;
  onUpdateSuggestion: (index: number, text: string) => void;
  onSetSuggestionRejected: (index: number, rejected: boolean) => void;
  onAddSuggestion: (text: string) => void;
  onConfirm: () => void;
  onRetryReview: () => void;
  onStartAutoLoop: () => void;
  onStopAutoLoop: () => void;
  onForceRelease: () => void;
  onCancel: () => void;
  onDelete: () => void;
  onSaveInstructions: (instructions: string) => Promise<void>;
}

const ReleaseDetail: React.FC<ReleaseDetailProps> = ({
  release,
  tasksMap,
  confirming,
  onStartSuggestionTask,
  onStartFixBatch,
  availableModels,
  fixModel,
  fixMode,
  onFixModelChange,
  onFixModeChange,
  onUpdateSuggestion,
  onSetSuggestionRejected,
  onAddSuggestion,
  onConfirm,
  onRetryReview,
  onStartAutoLoop,
  onStopAutoLoop,
  onForceRelease,
  onCancel,
  onDelete,
  onSaveInstructions,
}) => {
  const { theme } = useTheme();
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editText, setEditText] = useState<string>('');
  const [showAddSuggestion, setShowAddSuggestion] = useState(false);
  const [newSuggestionText, setNewSuggestionText] = useState<string>('');
  const [savingIndex, setSavingIndex] = useState<number | null>(null);
  const [addingSuggestion, setAddingSuggestion] = useState(false);
  const [instructionsText, setInstructionsText] = useState<string>(release.instructions || '');
  const [instructionsSaved, setInstructionsSaved] = useState(false);

  const handleInstructionsBlur = async () => {
    await onSaveInstructions(instructionsText);
    setInstructionsSaved(true);
    setTimeout(() => setInstructionsSaved(false), 2000);
  };

  // Allowed actions based on status
  const canConfirm = release.status === ReleaseStatus.NOTES_READY;
  const canRetryReview = release.status === ReleaseStatus.REVIEW_REJECTED && !release.auto_loop_active;
  const canStartAutoLoop = (
    release.status === ReleaseStatus.NOTES_READY
    || release.status === ReleaseStatus.REVIEW_REJECTED
  ) && !release.auto_loop_active;
  const canForceRelease = (
    release.status === ReleaseStatus.NOTES_READY
    || release.status === ReleaseStatus.REVIEW_REJECTED
    || release.status === ReleaseStatus.FAILED
  ) && !release.auto_loop_active;
  const canCancel = release.status !== ReleaseStatus.RELEASED
    && release.status !== ReleaseStatus.CANCELED;

  // Compute pending count to show next to confirm button
  const pendingTaskCount = release.suggestions.filter(s => {
    if (!s.task_id) return false;
    const t = tasksMap[s.task_id];
    return !t || t.status === TaskStatus.QUEUED || t.status === TaskStatus.IN_PROGRESS;
  }).length;

  const groupedSuggestions = groupByArea(release.suggestions);
  const pendingSuggestionCount = release.suggestions.filter(s => isPendingSuggestion(s, tasksMap)).length;

  const handleEditStart = (index: number) => {
    setEditingIndex(index);
    setEditText(release.suggestions[index].text);
  };

  const handleEditCancel = () => {
    setEditingIndex(null);
    setEditText('');
  };

  const handleEditSave = async (index: number) => {
    const trimmed = editText.trim();
    if (!trimmed) {
      alert('Suggestion text cannot be empty');
      return;
    }
    setSavingIndex(index);
    try {
      await onUpdateSuggestion(index, trimmed);
      setEditingIndex(null);
      setEditText('');
    } finally {
      setSavingIndex(null);
    }
  };

  const handleAddSuggestionSubmit = async () => {
    const trimmed = newSuggestionText.trim();
    if (!trimmed) {
      alert('Suggestion text cannot be empty');
      return;
    }
    setAddingSuggestion(true);
    try {
      await onAddSuggestion(trimmed);
      setShowAddSuggestion(false);
      setNewSuggestionText('');
    } finally {
      setAddingSuggestion(false);
    }
  };

  return (
    <div style={{
      backgroundColor: theme.surface,
      border: `1px solid ${theme.border}`,
      borderRadius: '8px',
      padding: '20px',
      minWidth: 0,
      overflowX: 'hidden',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
        <div>
          <h2 style={{ color: theme.text, margin: 0, fontSize: '20px' }}>{release.title}</h2>
          <div style={{ marginTop: '6px' }}>
            <StatusBadge status={release.status} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {canCancel && (
            <button
              onClick={onCancel}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: `1px solid ${theme.border}`,
                backgroundColor: theme.surface,
                color: theme.text,
                cursor: 'pointer',
                fontSize: '13px',
              }}
            >
              Cancel
            </button>
          )}
          <button
            onClick={onDelete}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: `1px solid ${theme.border}`,
              backgroundColor: theme.surface,
              color: '#ef4444',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            Delete
          </button>
        </div>
      </div>

      {release.error && (
        <div style={{
          padding: '12px',
          marginBottom: '12px',
          backgroundColor: '#ef444422',
          border: `1px solid #ef4444`,
          borderRadius: '6px',
          color: '#ef4444',
          fontSize: '13px',
          whiteSpace: 'pre-wrap',
        }}>
          <strong>Error:</strong> {release.error}
        </div>
      )}

      {release.auto_loop_active && (
        <div style={{
          padding: '12px',
          marginBottom: '12px',
          backgroundColor: '#3b82f622',
          border: `1px solid #3b82f6`,
          borderRadius: '6px',
          color: theme.text,
          fontSize: '13px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap',
        }}>
          <div>
            <strong style={{ color: '#3b82f6' }}>
              {release.status === ReleaseStatus.GENERATING_NOTES ? 'Auto-loop armed' : 'Auto-loop running'}
            </strong>
            {' - '}
            {release.status === ReleaseStatus.GENERATING_NOTES
              ? 'takes over the moment notes land: '
              : `iteration ${release.auto_loop_iterations || 0}/5: `}
            {release.fix_mode === 'per_suggestion'
              ? 'one fix task per pending suggestion'
              : 'one agent fixes every pending suggestion (grouped by area)'}
            , then re-review, until approved.
          </div>
          <button
            onClick={onStopAutoLoop}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: `1px solid ${theme.border}`,
              backgroundColor: theme.surface,
              color: theme.text,
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            Stop auto-loop
          </button>
        </div>
      )}

      {/* Release Notes */}
      <Section title="Release Notes">
        {release.status === ReleaseStatus.GENERATING_NOTES && !release.release_notes ? (
          <div style={{ color: theme.textSecondary, fontSize: '14px' }}>
            Running /quest-release-notes... (this can take a few minutes)
          </div>
        ) : release.release_notes ? (
          <pre style={{
            backgroundColor: theme.background,
            border: `1px solid ${theme.border}`,
            borderRadius: '6px',
            padding: '12px',
            color: theme.text,
            fontSize: '13px',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
            maxHeight: '400px',
            overflowY: 'auto',
            overflowX: 'auto',
            margin: 0,
          }}>{release.release_notes}</pre>
        ) : (
          <div style={{ color: theme.textSecondary, fontSize: '14px' }}>(no notes yet)</div>
        )}
      </Section>

      {/* Architecture Suggestions */}
      {(() => {
        const rejectedCount = release.suggestions.filter(s => s.rejected).length;
        const title = rejectedCount > 0
          ? `Architecture Review Suggestions (${release.suggestions.length - rejectedCount} active, ${rejectedCount} rejected)`
          : `Architecture Review Suggestions (${release.suggestions.length})`;
        return (
      <Section title={title}>
        {release.suggestions.length > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
            padding: '10px 12px', marginBottom: '10px',
            backgroundColor: theme.background, border: `1px solid ${theme.border}`, borderRadius: '6px',
          }}>
            <span style={{ color: theme.textSecondary, fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Fix with
            </span>
            <select
              value={fixModel}
              onChange={e => onFixModelChange(e.target.value)}
              title="Claude model for the fix tasks spawned from these suggestions"
              style={{ padding: '6px 10px', borderRadius: '6px', border: `1px solid ${theme.border}`, backgroundColor: theme.surface, color: theme.text, fontSize: '13px', maxWidth: '100%' }}
            >
              <option value="">System default model</option>
              {availableModels.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <select
              value={fixMode}
              onChange={e => onFixModeChange(e.target.value as ReleaseFixMode)}
              title="How pending suggestions become fix tasks"
              style={{ padding: '6px 10px', borderRadius: '6px', border: `1px solid ${theme.border}`, backgroundColor: theme.surface, color: theme.text, fontSize: '13px', maxWidth: '100%' }}
            >
              {(Object.keys(FIX_MODE_LABEL) as ReleaseFixMode[]).map(m => (
                <option key={m} value={m}>{FIX_MODE_LABEL[m]}</option>
              ))}
            </select>
            {pendingSuggestionCount > 0 && !release.auto_loop_active && (
              <button
                onClick={() => onStartFixBatch('all')}
                style={{ padding: '6px 14px', borderRadius: '6px', border: 'none', backgroundColor: '#f59e0b', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
              >
                {fixMode === 'per_suggestion'
                  ? `Start ${pendingSuggestionCount} quicktask${pendingSuggestionCount === 1 ? '' : 's'}`
                  : `Fix all ${pendingSuggestionCount} pending with one agent`}
              </button>
            )}
          </div>
        )}
        {release.suggestions.length === 0 ? (
          <div style={{ color: theme.textSecondary, fontSize: '14px' }}>
            {release.status === ReleaseStatus.GENERATING_NOTES
              ? 'Will appear once notes are generated.'
              : 'No suggestions found.'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {groupedSuggestions.map(([area, idxs]) => {
              const pendingInArea = idxs.filter(i => isPendingSuggestion(release.suggestions[i], tasksMap)).length;
              return (
              <div key={area}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: '12px', backgroundColor: theme.primary + '22', color: theme.primary,
                    fontSize: '12px', fontWeight: 700, letterSpacing: '0.03em',
                  }}>
                    {area}
                  </span>
                  <span style={{ color: theme.textSecondary, fontSize: '12px' }}>
                    {idxs.length} item{idxs.length === 1 ? '' : 's'}{pendingInArea > 0 ? `, ${pendingInArea} pending` : ''}
                  </span>
                  {pendingInArea > 0 && !release.auto_loop_active && groupedSuggestions.length > 1 && (
                    <button
                      onClick={() => onStartFixBatch('area', area)}
                      title={fixMode === 'per_suggestion' ? 'One quicktask per pending item in this area' : 'One agent fixes every pending item in this area'}
                      style={{ padding: '3px 10px', borderRadius: '6px', border: `1px solid #f59e0b`, backgroundColor: theme.background, color: '#f59e0b', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
                    >
                      Fix this area
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {idxs.map((idx) => {
              const s = release.suggestions[idx];
              const linkedTask = s.task_id ? tasksMap[s.task_id] : undefined;
              const isEditing = editingIndex === idx;
              const isRejected = !!s.rejected;
              return (
                <div
                  key={idx}
                  style={{
                    padding: '12px',
                    backgroundColor: isRejected ? theme.background : theme.background,
                    border: `1px solid ${isEditing ? theme.primary : isRejected ? '#6b7280' : theme.border}`,
                    borderRadius: '6px',
                    opacity: isRejected ? 0.6 : 1,
                  }}
                >
                  {isEditing ? (
                    <>
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px',
                          borderRadius: '4px',
                          border: `1px solid ${theme.border}`,
                          backgroundColor: theme.background,
                          color: theme.text,
                          fontSize: '13px',
                          fontFamily: 'inherit',
                          minHeight: '80px',
                          marginBottom: '8px',
                          boxSizing: 'border-box',
                        }}
                      />
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleEditSave(idx)}
                          disabled={savingIndex === idx}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: 'none',
                            backgroundColor: savingIndex === idx ? theme.border : '#10b981',
                            color: '#fff',
                            cursor: savingIndex === idx ? 'not-allowed' : 'pointer',
                            fontSize: '13px',
                            fontWeight: 600,
                          }}
                        >
                          {savingIndex === idx ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={handleEditCancel}
                          disabled={savingIndex === idx}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: `1px solid ${theme.border}`,
                            backgroundColor: theme.background,
                            color: theme.text,
                            cursor: savingIndex === idx ? 'not-allowed' : 'pointer',
                            fontSize: '13px',
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                        {isRejected && (
                          <span style={{
                            flexShrink: 0,
                            padding: '2px 6px',
                            borderRadius: '4px',
                            backgroundColor: '#6b728022',
                            color: '#6b7280',
                            fontSize: '11px',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            marginTop: '1px',
                          }}>
                            Rejected
                          </span>
                        )}
                        <div style={{
                          color: isRejected ? theme.textSecondary : theme.text,
                          fontSize: '14px',
                          whiteSpace: 'pre-wrap',
                          overflowWrap: 'break-word',
                          wordBreak: 'break-word',
                          textDecoration: isRejected ? 'line-through' : 'none',
                          minWidth: 0,
                          flex: 1,
                        }}>
                          {s.text}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        {isRejected ? (
                          <button
                            onClick={() => onSetSuggestionRejected(idx, false)}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '6px',
                              border: `1px solid ${theme.border}`,
                              backgroundColor: theme.background,
                              color: theme.textSecondary,
                              cursor: 'pointer',
                              fontSize: '13px',
                            }}
                          >
                            Un-reject
                          </button>
                        ) : isPendingSuggestion(s, tasksMap) ? (
                          <>
                            {s.task_id && (
                              <span style={{ color: theme.textSecondary, fontSize: '12px' }}>
                                Previous task #{s.task_id} {linkedTask?.status === TaskStatus.DISMISSED ? 'dismissed' : 'failed'}, try again:
                              </span>
                            )}
                            <button
                              onClick={() => handleEditStart(idx)}
                              style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                border: `1px solid ${theme.border}`,
                                backgroundColor: theme.background,
                                color: theme.text,
                                cursor: 'pointer',
                                fontSize: '13px',
                              }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => onStartSuggestionTask(idx)}
                              style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                border: 'none',
                                backgroundColor: '#f59e0b',
                                color: '#fff',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: 600,
                              }}
                            >
                              {s.task_id ? 'Retry Quicktask' : 'Start Quicktask'}
                            </button>
                            <button
                              onClick={() => onSetSuggestionRejected(idx, true)}
                              style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                border: `1px solid #ef4444`,
                                backgroundColor: theme.background,
                                color: '#ef4444',
                                cursor: 'pointer',
                                fontSize: '13px',
                              }}
                            >
                              Reject
                            </button>
                          </>
                        ) : (
                          <>
                            <span style={{ color: theme.textSecondary, fontSize: '12px' }}>
                              Task #{s.task_id}
                            </span>
                            <TaskStatusBadge task={linkedTask} />
                            <a
                              href={`/quicktasks?task=${s.task_id}`}
                              style={{
                                fontSize: '12px',
                                color: theme.primary,
                                textDecoration: 'none',
                              }}
                            >
                              View →
                            </a>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
                })}
                </div>
              </div>
              );
            })}
          </div>
        )}
        <div style={{ marginTop: '12px' }}>
          {showAddSuggestion ? (
            <div style={{
              padding: '12px',
              backgroundColor: theme.background,
              border: `1px solid ${theme.primary}`,
              borderRadius: '6px',
            }}>
              <textarea
                value={newSuggestionText}
                onChange={(e) => setNewSuggestionText(e.target.value)}
                placeholder="Enter a new suggestion..."
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: `1px solid ${theme.border}`,
                  backgroundColor: theme.background,
                  color: theme.text,
                  fontSize: '13px',
                  fontFamily: 'inherit',
                  minHeight: '80px',
                  marginBottom: '8px',
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  onClick={handleAddSuggestionSubmit}
                  disabled={addingSuggestion}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    backgroundColor: addingSuggestion ? theme.border : '#10b981',
                    color: '#fff',
                    cursor: addingSuggestion ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                    fontWeight: 600,
                  }}
                >
                  {addingSuggestion ? 'Adding...' : 'Add Suggestion'}
                </button>
                <button
                  onClick={() => {
                    setShowAddSuggestion(false);
                    setNewSuggestionText('');
                  }}
                  disabled={addingSuggestion}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: `1px solid ${theme.border}`,
                    backgroundColor: theme.background,
                    color: theme.text,
                    cursor: addingSuggestion ? 'not-allowed' : 'pointer',
                    fontSize: '13px',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowAddSuggestion(true)}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: `1px solid ${theme.border}`,
                backgroundColor: theme.background,
                color: theme.text,
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 600,
              }}
            >
              + Add More Suggestion
            </button>
          )}
        </div>
      </Section>
        );
      })()}

      {/* Confirm button (initial path from NOTES_READY) */}
      {canConfirm && (
        <div style={{ marginTop: '16px', padding: '12px', backgroundColor: theme.background, borderRadius: '6px', border: `1px solid ${theme.border}` }}>
          <div style={{ marginBottom: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <span style={{ color: theme.textSecondary, fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Instructions (optional)
              </span>
              {instructionsSaved && (
                <span style={{ color: '#10b981', fontSize: '11px' }}>Saved</span>
              )}
            </div>
            <textarea
              value={instructionsText}
              onChange={e => setInstructionsText(e.target.value)}
              onBlur={handleInstructionsBlur}
              placeholder="Notes or guidance for this release run..."
              rows={3}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '8px',
                borderRadius: '4px',
                border: `1px solid ${theme.border}`,
                backgroundColor: theme.surface,
                color: theme.text,
                fontSize: '13px',
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
            />
          </div>
          {pendingTaskCount > 0 && (
            <div style={{ color: theme.textSecondary, fontSize: '13px', marginBottom: '8px' }}>
              {pendingTaskCount} fix task{pendingTaskCount === 1 ? '' : 's'} still running. Confirming will wait for them to finish before re-reviewing and releasing.
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              onClick={onConfirm}
              disabled={confirming}
              style={{
                padding: '12px 24px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: confirming ? theme.border : '#10b981',
                color: '#fff',
                cursor: confirming ? 'not-allowed' : 'pointer',
                fontSize: '15px',
                fontWeight: 600,
              }}
            >
              {confirming ? 'Confirming...' : 'Confirm Release (run /release-quest)'}
            </button>
            {canStartAutoLoop && (
              <button
                onClick={onStartAutoLoop}
                style={{
                  padding: '12px 20px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#3b82f6',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                Run Autonomously Until Approved
              </button>
            )}
            {canForceRelease && (
              <button
                onClick={onForceRelease}
                style={{
                  padding: '12px 20px',
                  borderRadius: '6px',
                  border: `1px solid ${theme.border}`,
                  backgroundColor: theme.surface,
                  color: theme.text,
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                Force Release (skip re-review)
              </button>
            )}
          </div>
        </div>
      )}

      {/* Post-rejection action panel — user is unblocked here */}
      {release.status === ReleaseStatus.REVIEW_REJECTED && (
        <div style={{
          marginTop: '16px',
          padding: '14px',
          backgroundColor: '#f9731622',
          borderRadius: '6px',
          border: `1px solid #f97316`,
        }}>
          <div style={{ color: theme.text, fontSize: '14px', fontWeight: 600, marginBottom: '6px' }}>
            Re-review NOT APPROVED — what next?
          </div>
          <div style={{ color: theme.textSecondary, fontSize: '13px', marginBottom: '12px' }}>
            Any new suggestions from the reviewer have been added above. Hand
            them to one fixing agent (or start quicktasks), then re-run the
            review. You can also let it iterate autonomously, or override the
            AI and release anyway.
          </div>
          <div style={{ marginBottom: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <span style={{ color: theme.textSecondary, fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Instructions (optional)
              </span>
              {instructionsSaved && (
                <span style={{ color: '#10b981', fontSize: '11px' }}>Saved</span>
              )}
            </div>
            <textarea
              value={instructionsText}
              onChange={e => setInstructionsText(e.target.value)}
              onBlur={handleInstructionsBlur}
              placeholder="Notes or guidance for this release run..."
              rows={3}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '8px',
                borderRadius: '4px',
                border: `1px solid ${theme.border}`,
                backgroundColor: theme.surface,
                color: theme.text,
                fontSize: '13px',
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
            />
          </div>
          {pendingTaskCount > 0 && (
            <div style={{ color: theme.textSecondary, fontSize: '13px', marginBottom: '8px' }}>
              {pendingTaskCount} fix task{pendingTaskCount === 1 ? '' : 's'} still running.
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {canRetryReview && (
              <button
                onClick={onRetryReview}
                disabled={confirming}
                style={{
                  padding: '10px 18px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: confirming ? theme.border : '#10b981',
                  color: '#fff',
                  cursor: confirming ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                {confirming ? 'Re-running...' : 'Re-run Review'}
              </button>
            )}
            {canStartAutoLoop && (
              <button
                onClick={onStartAutoLoop}
                style={{
                  padding: '10px 18px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#3b82f6',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                Run Autonomously Until Approved
              </button>
            )}
            {canForceRelease && (
              <button
                onClick={onForceRelease}
                style={{
                  padding: '10px 18px',
                  borderRadius: '6px',
                  border: `1px solid ${theme.border}`,
                  backgroundColor: theme.surface,
                  color: theme.text,
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                Manually Approve and Release
              </button>
            )}
          </div>
        </div>
      )}

      {/* Force-release available from FAILED too */}
      {release.status === ReleaseStatus.FAILED && canForceRelease && (
        <div style={{ marginTop: '16px', padding: '12px', backgroundColor: theme.background, borderRadius: '6px', border: `1px solid ${theme.border}` }}>
          <div style={{ color: theme.textSecondary, fontSize: '13px', marginBottom: '8px' }}>
            Release failed — you can still manually approve and run /release-quest.
          </div>
          <button
            onClick={onForceRelease}
            style={{
              padding: '10px 18px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: '#10b981',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            Manually Approve and Release
          </button>
        </div>
      )}

      {/* Re-review output */}
      {release.review_output && (
        <Section title={`Re-review${release.review_passed === false ? ' (NOT APPROVED)' : ''}`}>
          <pre style={{
            backgroundColor: theme.background,
            border: `1px solid ${theme.border}`,
            borderRadius: '6px',
            padding: '12px',
            color: theme.text,
            fontSize: '13px',
            whiteSpace: 'pre-wrap',
            maxHeight: '300px',
            overflowY: 'auto',
            margin: 0,
          }}>{release.review_output}</pre>
        </Section>
      )}

      {/* Release output */}
      {release.release_output && (
        <Section title="Release Output (/release-quest)">
          <pre style={{
            backgroundColor: theme.background,
            border: `1px solid ${theme.border}`,
            borderRadius: '6px',
            padding: '12px',
            color: theme.text,
            fontSize: '13px',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
            maxHeight: '400px',
            overflowY: 'auto',
            overflowX: 'auto',
            margin: 0,
          }}>{release.release_output}</pre>
        </Section>
      )}
    </div>
  );
};

interface ConfirmModalProps {
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({ title, message, confirmLabel, onConfirm, onCancel }) => {
  const { theme } = useTheme();
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onCancel}
    >
      <div
        style={{
          backgroundColor: theme.surface,
          border: `1px solid ${theme.border}`,
          borderRadius: '10px',
          padding: '24px',
          maxWidth: '440px',
          width: '90%',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <h3 style={{ color: theme.text, margin: '0 0 12px', fontSize: '17px', fontWeight: 600 }}>{title}</h3>
        <p style={{ color: theme.textSecondary, margin: '0 0 24px', fontSize: '14px', lineHeight: 1.5 }}>{message}</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button
            onClick={onCancel}
            style={{
              padding: '9px 18px',
              borderRadius: '6px',
              border: `1px solid ${theme.border}`,
              backgroundColor: theme.background,
              color: theme.text,
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: '9px 18px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: '#10b981',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
  const { theme } = useTheme();
  return (
    <div style={{ marginTop: '16px' }}>
      <h3 style={{ color: theme.text, fontSize: '14px', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {title}
      </h3>
      {children}
    </div>
  );
};

export default Releases;
